import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useCreateClubMutation, useUpdateClubMutation } from '../../redux';
import { useGetAllAffiliationsQuery } from '../../redux/services/affiliation-api';
import { useAuthContext } from '../../context/auth-context';
import { useIsMobile } from '../../hooks/use-is-mobile';

const PRIMARY = '#0F6E56';
const DRAWER_WIDTH = 320;

const validationSchema = Yup.object({
  name: Yup.string()
    .min(3, 'Mínimo 3 caracteres')
    .max(30, 'Máximo 30 caracteres')
    .required('Requerido'),
  affiliatedTo: Yup.string().required('Selecciona una zona'),
  director: Yup.string().max(50, 'Máximo 50 caracteres'),
  status: Yup.boolean(),
});

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function ClubDrawer({ open, mode, club, onClose, onDeleteRequest }) {
  const { activeCamp } = useAuthContext();
  const isMobile = useIsMobile();
  const [createClub] = useCreateClubMutation();
  const [updateClub] = useUpdateClubMutation();

  const { data: affiliations = [], isLoading: loadingAffiliations } = useGetAllAffiliationsQuery(
    activeCamp?._id ? { campId: activeCamp._id } : undefined,
    { skip: !activeCamp?._id }
  );

  const isEdit = mode === 'edit';

  const initialAffiliationId =
    typeof club?.affiliatedTo === 'object' && club?.affiliatedTo !== null
      ? club.affiliatedTo._id
      : club?.affiliatedTo || '';

  const formik = useFormik({
    initialValues: {
      name: club?.name || '',
      director: club?.director || '',
      affiliatedTo: initialAffiliationId,
      status: club?.status !== undefined ? club.status : true,
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      let result;
      if (isEdit) {
        result = await updateClub({
          _id: club._id,
          name: values.name,
          director: values.director,
          affiliatedTo: values.affiliatedTo,
          status: values.status,
          subdirector: club.subdirector,
          totalAttendance: club.totalAttendance,
        });
      } else {
        result = await createClub({
          name: values.name,
          director: values.director,
          affiliatedTo: values.affiliatedTo,
          status: values.status,
          camps: activeCamp?._id ? [activeCamp._id] : [],
        });
      }
      setSubmitting(false);
      if (!result.error) onClose();
    },
  });

  useEffect(() => {
    if (!open) formik.resetForm();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const formFields = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <TextField
        label="Nombre"
        name="name"
        value={formik.values.name}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.name && Boolean(formik.errors.name)}
        helperText={formik.touched.name && formik.errors.name}
        required
        fullWidth
        size="small"
      />

      <TextField
        label="Director"
        name="director"
        value={formik.values.director}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.director && Boolean(formik.errors.director)}
        helperText={formik.touched.director && formik.errors.director}
        fullWidth
        size="small"
      />

      <FormControl
        fullWidth
        size="small"
        required
        error={formik.touched.affiliatedTo && Boolean(formik.errors.affiliatedTo)}
      >
        <InputLabel>Zona (Afiliado A)</InputLabel>
        <Select
          name="affiliatedTo"
          value={formik.values.affiliatedTo}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          label="Zona (Afiliado A)"
          disabled={loadingAffiliations}
        >
          {loadingAffiliations ? (
            <MenuItem disabled><CircularProgress size={16} /></MenuItem>
          ) : (
            affiliations.map(aff => (
              <MenuItem key={aff._id} value={aff._id}>
                {aff.name}{aff.zone ? ` — ${aff.zone}` : ''}
              </MenuItem>
            ))
          )}
        </Select>
        {formik.touched.affiliatedTo && formik.errors.affiliatedTo && (
          <FormHelperText>{formik.errors.affiliatedTo}</FormHelperText>
        )}
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel>Estado</InputLabel>
        <Select
          name="status"
          value={formik.values.status}
          onChange={formik.handleChange}
          label="Estado"
        >
          <MenuItem value={true}>Activo</MenuItem>
          <MenuItem value={false}>Inactivo</MenuItem>
        </Select>
      </FormControl>

      {(activeCamp || (isEdit && club?.createdAt)) && (
        <>
          <Divider />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {activeCamp && (
              <Typography variant="caption" color="text.secondary">
                Campamento: <strong>{activeCamp.name}</strong>
              </Typography>
            )}
            {isEdit && club?.createdAt && (
              <Typography variant="caption" color="text.secondary">
                Creado: <strong>{formatDate(club.createdAt)}</strong>
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );

  const submitButton = (
    <Button
      type="submit"
      size="small"
      variant="contained"
      disabled={formik.isSubmitting}
      sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#0a5a45' }, textTransform: 'none' }}
    >
      {formik.isSubmitting ? (
        <CircularProgress size={16} color="inherit" />
      ) : isEdit ? 'Guardar cambios' : 'Crear club'}
    </Button>
  );

  if (isMobile) {
    return (
      <Dialog open={open} onClose={onClose} fullWidth fullScreen>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}>
          {isEdit ? <EditIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <AddIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
          <span style={{ flex: 1 }}>{isEdit ? 'Editar club' : 'Nuevo club'}</span>
          <IconButton size="small" onClick={onClose} edge="end">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="club-form-mobile" onSubmit={formik.handleSubmit} sx={{ pt: 1 }}>
            {formFields}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
          {isEdit && (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => onDeleteRequest(club)}
              sx={{ mr: 'auto', textTransform: 'none' }}
            >
              Deshabilitar
            </Button>
          )}
          <Button size="small" onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            disabled={formik.isSubmitting}
            onClick={() => formik.handleSubmit()}
            sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#0a5a45' }, textTransform: 'none' }}
          >
            {formik.isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : isEdit ? 'Guardar cambios' : 'Crear club'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      variant="persistent"
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        overflow: 'hidden',
        transition: theme => theme.transitions.create('width'),
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          position: 'relative',
          height: '100%',
          border: 'none',
          borderLeft: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {isEdit ? (
            <EditIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          ) : (
            <AddIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          )}
          <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
            {isEdit ? 'Editar club' : 'Nuevo club'}
          </Typography>
          {isEdit && club?._id && (
            <Typography variant="caption" color="text.disabled" sx={{ mr: 1 }}>
              #{club._id.slice(-6)}
            </Typography>
          )}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Form */}
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}
        >
          <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {formFields}
          </Box>

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Footer */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {isEdit && (
              <Button
                size="small"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => onDeleteRequest(club)}
                sx={{ mr: 'auto', textTransform: 'none' }}
              >
                Deshabilitar
              </Button>
            )}
            <Button size="small" onClick={onClose} sx={{ textTransform: 'none' }}>
              Cancelar
            </Button>
            {submitButton}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
